import { useEffect, useState, type ReactNode } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button, Icon, Label, Select, Text, TextInput } from '@gravity-ui/uikit';
import { Handset, Magnifier } from '@gravity-ui/icons';
import type { ManagerTenantCounts, ManagerTenantSummary, SubscriptionState } from '@odonix/contracts';
import { useTenantsApi } from '../../lib/api/context.js';
import type { TenantsSearch } from '../../app/router.js';
import { Page } from '../../ui/page.js';
import { useGo } from '../../ui/router-link.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';
import { CallLink } from './contact-links.js';
import { KIND_LABEL, STATE_LABEL, STATE_TONE, deadline } from './labels.js';

/**
 * Клиники менеджера — только закреплённые за ним. Порядок по умолчанию —
 * «у кого раньше кончается»: первые строки — те, кому пора платить.
 */

const routeApi = getRouteApi('/shell/tenants');
const PAGE_SIZE = 50;
const STATE_ORDER: readonly SubscriptionState[] = ['trial', 'active', 'grace', 'restricted', 'suspended'];

export function TenantsScreen() {
  const api = useTenantsApi();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const [qDraft, setQDraft] = useState(search.q ?? '');

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = qDraft.trim();
      if (q !== (search.q ?? '')) void navigate({ search: (prev) => ({ ...prev, q: q === '' ? undefined : q }), replace: true });
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [qDraft, search.q, navigate]);

  const query = useInfiniteQuery({
    queryKey: ['tenants', search],
    queryFn: ({ pageParam }) =>
      api.list({
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort: search.sort ?? 'ending',
        ...(search.q === undefined ? {} : { q: search.q }),
        ...(search.kind === undefined ? {} : { kind: search.kind }),
        ...(search.state === undefined ? {} : { state: search.state }),
        ...(search.ending === undefined ? {} : { endingWithinDays: search.ending }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
  });

  const tenants = query.data?.pages.flatMap((page) => page.tenants) ?? [];
  const counts = query.data?.pages[0]?.counts;
  const filtered = search.q !== undefined || search.kind !== undefined || search.state !== undefined || search.ending !== undefined;

  function setSearch(patch: Partial<TenantsSearch>): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  return (
    <Page title="Мои клиники" description="Только те, что закрепили за вами: пришли по вашей ссылке или их закрепил сотрудник Odonix.">
      <div className="flex flex-col gap-3">
        <TextInput
          size="l"
          placeholder="Название, телефон, почта, ИНН"
          value={qDraft}
          onUpdate={setQDraft}
          hasClear
          startContent={<Icon data={Magnifier} size={16} className="ml-2 text-[var(--g-color-text-secondary)]" />}
          controlProps={{ 'aria-label': 'Поиск', inputMode: 'search' }}
        />
        <div className="flex flex-wrap gap-2">
          <Filter
            pressed={search.kind === undefined && search.ending === undefined && search.state === undefined}
            onClick={() => {
              setSearch({ kind: undefined, ending: undefined, state: undefined });
            }}
          >
            Все {counts === undefined ? '' : counts.all}
          </Filter>
          <Filter
            pressed={search.kind === 'clinic'}
            onClick={() => {
              setSearch({ kind: search.kind === 'clinic' ? undefined : 'clinic' });
            }}
          >
            Клиники {counts === undefined ? '' : counts.clinic}
          </Filter>
          <Filter
            pressed={search.kind === 'lab'}
            onClick={() => {
              setSearch({ kind: search.kind === 'lab' ? undefined : 'lab' });
            }}
          >
            Лаборатории {counts === undefined ? '' : counts.lab}
          </Filter>
          <Filter
            pressed={search.ending === 7}
            onClick={() => {
              setSearch({ ending: search.ending === 7 ? undefined : 7, state: undefined });
            }}
          >
            Кончается за неделю {counts === undefined ? '' : counts.endingSoon}
          </Filter>
          {STATE_ORDER.map((state) => (
            <Filter
              key={state}
              pressed={search.state === state}
              onClick={() => {
                setSearch({ state: search.state === state ? undefined : state, ending: undefined });
              }}
            >
              {STATE_LABEL[state]} {counts === undefined ? '' : counts.byState[state]}
            </Filter>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3">
          <Text color="secondary">{counts === undefined ? 'Загрузка…' : query.hasNextPage ? `Показано ${String(tenants.length)}` : `Всего: ${String(tenants.length)}`}</Text>
          <Select
            size="m"
            width={240}
            value={[search.sort ?? 'ending']}
            options={[
              { value: 'ending', content: 'Сначала — у кого кончается' },
              { value: 'newest', content: 'Сначала новые' },
              { value: 'name', content: 'По названию' },
            ]}
            onUpdate={(values) => {
              const value = values[0];
              setSearch({ sort: value === 'newest' || value === 'name' ? value : undefined });
            }}
          />
        </div>
      </div>

      {query.isError ? (
        <ErrorNote title="Список не загрузился" error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <LoadingRows rows={6} height={72} />
      ) : tenants.length === 0 ? (
        <Empty
          title={filtered ? 'По этому отбору никого нет' : 'Клиник пока нет'}
          description={filtered ? 'Снимите фильтр или поищите по названию.' : 'Клиника появится здесь, когда зарегистрируется по вашей ссылке.'}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {tenants.map((tenant) => (
              <TenantRow key={tenant.id} tenant={tenant} />
            ))}
          </ul>
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button view="outlined" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                Показать ещё
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Page>
  );
}

function Filter({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Button view={pressed ? 'action' : 'outlined'} size="m" onClick={onClick}>
      {children}
    </Button>
  );
}

const DEADLINE_COLOR = { danger: 'danger', warning: 'warning', muted: 'secondary' } as const;

function TenantRow({ tenant }: { tenant: ManagerTenantSummary }) {
  const go = useGo();
  const due = deadline(tenant);
  const phone = tenant.phone ?? tenant.ownerPhone;
  const whom = tenant.phone === null ? (tenant.ownerName ?? 'владелец') : tenant.name;
  return (
    <li className="flex items-center gap-3 rounded-xl border border-[var(--g-color-line-generic)] bg-[var(--g-color-base-background)] p-3">
      <button
        type="button"
        className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-1 border-0 bg-transparent p-0 text-left"
        onClick={() => {
          go(`/tenants/${tenant.id}`);
        }}
      >
        <span className="flex w-full min-w-0 items-center gap-2">
          <Text variant="subheader-2" ellipsis>
            {tenant.name}
          </Text>
          <Text variant="caption-2" color="secondary" className="shrink-0">
            {KIND_LABEL[tenant.kind]}
          </Text>
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <Label theme={STATE_TONE[tenant.subscriptionState]} size="xs">
            {STATE_LABEL[tenant.subscriptionState]}
          </Label>
          {due === null ? null : (
            <Text variant="body-1" color={DEADLINE_COLOR[due.tone]} className="tabular">
              {due.text}
            </Text>
          )}
        </span>
        {tenant.ownerName === null && tenant.address === null ? null : (
          <Text variant="caption-2" color="secondary" ellipsis className="w-full">
            {[tenant.ownerName, tenant.address].filter((part) => part !== null).join(' · ')}
          </Text>
        )}
      </button>
      {phone === null ? (
        <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--g-color-base-generic)] text-[var(--g-color-text-hint)]" title="Телефона нет">
          <Icon data={Handset} size={18} />
        </span>
      ) : (
        <CallLink tenantId={tenant.id} phone={phone} label={`Позвонить: ${whom}, ${phone}`} className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--g-color-base-positive-heavy)] text-white">
          <Icon data={Handset} size={18} />
        </CallLink>
      )}
    </li>
  );
}

export type { ManagerTenantCounts };
